import { Button, Card, CardContent, CardHeader, CardTitle, Input } from "@aix/ui"
import { Mail, Save, Shield } from "lucide-react"
import React, { useState } from "react"
import toast from "react-hot-toast"
import { trpc } from "~/lib/trpc"

export const ProviderSettings: React.FC = () => {
  const [provider, setProvider] = useState<"ses" | "sendgrid" | "postmark" | "smtp">("ses")
  const [sendingDomain, setSendingDomain] = useState("")
  const [awsRegion, setAwsRegion] = useState("us-east-1")
  const [awsAccessKeyId, setAwsAccessKeyId] = useState("")
  const [awsSecretAccessKey, setAwsSecretAccessKey] = useState("")
  const [apiKey, setApiKey] = useState("")
  const [dailyLimit, setDailyLimit] = useState("50000")
  const [hourlyLimit, setHourlyLimit] = useState("14")

  const configureProvider = trpc.emailMarketing.configureProvider.useMutation({
    onSuccess: () => {
      toast.success("Provider settings saved successfully")
      // Clear sensitive fields
      setAwsAccessKeyId("")
      setAwsSecretAccessKey("")
      setApiKey("")
    },
    onError: (error) => {
      toast.error(`Failed to save settings: ${error.message}`)
    },
  })

  const handleSave = () => {
    if (!sendingDomain.trim()) {
      toast.error("Please enter a sending domain")
      return
    }

    if (provider === "ses" && (!awsAccessKeyId || !awsSecretAccessKey)) {
      toast.error("Please enter AWS credentials")
      return
    }

    configureProvider.mutate({
      provider,
      sendingDomain,
      awsRegion: provider === "ses" ? awsRegion : undefined,
      awsAccessKeyId: provider === "ses" ? awsAccessKeyId : undefined,
      awsSecretAccessKey: provider === "ses" ? awsSecretAccessKey : undefined,
      apiKey: provider !== "ses" ? apiKey : undefined,
      dailyLimit: parseInt(dailyLimit),
      hourlyLimit: parseInt(hourlyLimit),
    })
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Email Provider Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground">Provider</label>
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value as any)}
                className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background text-foreground"
              >
                <option value="ses">Amazon SES</option>
                <option value="sendgrid">SendGrid</option>
                <option value="postmark">Postmark</option>
                <option value="smtp">Custom SMTP</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground">Sending Domain</label>
              <Input
                value={sendingDomain}
                onChange={(e) => setSendingDomain(e.target.value)}
                placeholder="yourdomain.com"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">The domain you&apos;ll be sending emails from</p>
            </div>

            {provider === "ses" && (
              <>
                <div>
                  <label className="text-sm font-medium text-foreground">AWS Region</label>
                  <select
                    value={awsRegion}
                    onChange={(e) => setAwsRegion(e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background text-foreground"
                  >
                    <option value="us-east-1">US East (N. Virginia)</option>
                    <option value="us-west-2">US West (Oregon)</option>
                    <option value="eu-west-1">EU (Ireland)</option>
                    <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground">AWS Access Key ID</label>
                  <Input
                    type="password"
                    value={awsAccessKeyId}
                    onChange={(e) => setAwsAccessKeyId(e.target.value)}
                    placeholder="AKIA..."
                    className="mt-1"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground">AWS Secret Access Key</label>
                  <Input
                    type="password"
                    value={awsSecretAccessKey}
                    onChange={(e) => setAwsSecretAccessKey(e.target.value)}
                    placeholder="Your secret key"
                    className="mt-1"
                  />
                </div>
              </>
            )}

            {provider !== "ses" && (
              <div>
                <label className="text-sm font-medium text-foreground">API Key</label>
                <Input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Your API key"
                  className="mt-1"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground">Daily Send Limit</label>
                <Input
                  type="number"
                  value={dailyLimit}
                  onChange={(e) => setDailyLimit(e.target.value)}
                  placeholder="50000"
                  className="mt-1"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-foreground">Hourly Rate Limit</label>
                <Input
                  type="number"
                  value={hourlyLimit}
                  onChange={(e) => setHourlyLimit(e.target.value)}
                  placeholder="14"
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">Emails per second</p>
              </div>
            </div>

            <Button onClick={handleSave} loading={configureProvider.isLoading} className="w-full">
              <Save className="w-4 h-4 mr-2" />
              Save Provider Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Domain Verification</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-secondary rounded-lg">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-primary mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium mb-2">DKIM Configuration</h4>
                  <p className="text-sm text-muted-foreground mb-2">Add these CNAME records to your DNS:</p>
                  <code className="block text-xs bg-background p-2 rounded border border-border">
                    {`_domainkey.${sendingDomain || "yourdomain.com"}`}
                  </code>
                </div>
              </div>
            </div>

            <div className="p-4 bg-secondary rounded-lg">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-primary mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium mb-2">SPF Record</h4>
                  <p className="text-sm text-muted-foreground mb-2">Add this TXT record to your DNS:</p>
                  <code className="block text-xs bg-background p-2 rounded border border-border">
                    v=spf1 include:amazonses.com ~all
                  </code>
                </div>
              </div>
            </div>

            <div className="p-4 bg-secondary rounded-lg">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-primary mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium mb-2">DMARC Policy</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Add this TXT record to _dmarc.{sendingDomain || "yourdomain.com"}:
                  </p>
                  <code className="block text-xs bg-background p-2 rounded border border-border">
                    v=DMARC1; p=quarantine; rua=mailto:dmarc@{sendingDomain || "yourdomain.com"}
                  </code>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Cost Calculator</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-secondary rounded-lg">
                <p className="text-sm text-muted-foreground">AWS SES Cost</p>
                <p className="text-xl font-bold">$0.10</p>
                <p className="text-xs text-muted-foreground">per 1,000 emails</p>
              </div>

              <div className="p-4 bg-secondary rounded-lg">
                <p className="text-sm text-muted-foreground">Monthly Estimate</p>
                <p className="text-xl font-bold">${(((parseInt(dailyLimit) || 0) * 30 * 0.1) / 1000).toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">Based on {dailyLimit} emails/day</p>
              </div>
            </div>

            <div className="text-xs text-muted-foreground">
              <p>• No monthly fees or setup costs</p>
              <p>• Pay only for what you send</p>
              <p>• First 62,000 emails per month are free when sent from EC2</p>
              <p>• Additional data transfer charges may apply for attachments</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
